import { API_BASE_URL } from '../config/api';

export interface MealPlanItem {
  foodName: string;
  quantity: number;
  unit: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  notes?: string;
}

export interface MealPlan {
  mealType: string;
  items: MealPlanItem[];
  notes?: string;
}

export interface DailyMealPlan {
  date: string;
  meals: MealPlan[];
  dailyNotes?: string;
}

export interface DietProgram {
  id: string;
  dietitianId: string;
  clientId: string;
  clientName: string;
  programName: string;
  programType: string;
  startDate: string;
  endDate: string;
  description?: string;
  goals?: string;
  restrictions?: string;
  notes?: string;
  status: string;
  targetWeight?: number;
  currentWeight?: number;
  pdfUrl?: string;
  dailyMealPlans?: DailyMealPlan[];
  createdAt: string;
  updatedAt?: string;
}

export interface CreateDietProgramRequest {
  dietitianId: string;
  clientId: string;
  clientName: string;
  programName: string;
  programType: string;
  startDate: string;
  endDate: string;
  description?: string;
  goals?: string;
  restrictions?: string;
  notes?: string;
  status?: string;
  targetWeight?: number;
  currentWeight?: number;
  pdfUrl?: string;
  dailyMealPlans?: DailyMealPlan[];
}

export interface CreateDietProgramResponse {
  message: string;
  dietProgram: DietProgram;
}

export interface UpdateDietProgramRequest {
  clientId?: string;
  clientName?: string;
  programName?: string;
  programType?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  goals?: string;
  restrictions?: string;
  notes?: string;
  status?: string;
  targetWeight?: number;
  currentWeight?: number;
  pdfUrl?: string;
  dailyMealPlans?: DailyMealPlan[];
}

export interface UpdateDietProgramResponse {
  message: string;
  dietProgram: DietProgram;
}

export interface DeleteDietProgramResponse {
  message: string;
}

export interface ErrorResponse {
  error: string;
}

export interface TestResponse {
  message: string;
  timestamp: string;
}

export const dietProgramApi = {
  async getAllDietPrograms(): Promise<DietProgram[]> {
    const response = await fetch(`${API_BASE_URL}/dietprograms`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getDietProgramById(id: string): Promise<DietProgram> {
    const response = await fetch(`${API_BASE_URL}/dietprograms/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getDietProgramsByClient(clientId: string): Promise<DietProgram[]> {
    const response = await fetch(
      `${API_BASE_URL}/dietprograms/client/${clientId}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getDietProgramsByDietitian(dietitianId: string): Promise<DietProgram[]> {
    const response = await fetch(
      `${API_BASE_URL}/dietprograms/dietitian/${encodeURIComponent(dietitianId)}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getActiveDietPrograms(): Promise<DietProgram[]> {
    const response = await fetch(`${API_BASE_URL}/dietprograms/active`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async createDietProgram(
    dietProgramData: CreateDietProgramRequest
  ): Promise<CreateDietProgramResponse> {
    const response = await fetch(`${API_BASE_URL}/dietprograms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dietProgramData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async updateDietProgram(
    id: string,
    dietProgramData: UpdateDietProgramRequest
  ): Promise<UpdateDietProgramResponse> {
    const response = await fetch(`${API_BASE_URL}/dietprograms/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dietProgramData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async deleteDietProgram(id: string): Promise<DeleteDietProgramResponse> {
    const response = await fetch(`${API_BASE_URL}/dietprograms/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async testConnection(): Promise<TestResponse> {
    const response = await fetch(`${API_BASE_URL}/dietprograms/test`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },
};
